import React from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { cn } from '../../utils/cn';
import { useOnboarding } from '../../onboarding/useOnboardingStore';

export interface HelpHintProps {
  /** Stable id; once dismissed the hint stays hidden for this user. */
  id: string;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Optional secondary action, e.g. "Show me how" to start a tour. */
  action?: { label: string; onClick: () => void };
  tone?: 'neutral' | 'info';
  className?: string;
}

export const HelpHint: React.FC<HelpHintProps> = ({
  id,
  title,
  children,
  action,
  tone = 'neutral',
  className,
}) => {
  const { isHintDismissed, dismissHint } = useOnboarding();
  if (isHintDismissed(id)) return null;

  return (
    <div
      role="note"
      className={cn(
        'relative flex gap-3 rounded-2xl border px-4 py-3.5',
        tone === 'info'
          ? 'border-blue-500/25 bg-blue-500/[0.07]'
          : 'border-white/10 bg-white/[0.03]',
        className,
      )}
    >
      <FiInfo
        size={16}
        aria-hidden
        className={cn('mt-0.5 shrink-0', tone === 'info' ? 'text-blue-400' : 'text-zinc-400')}
      />
      <div className="min-w-0 flex-1 pr-5">
        {title && <p className="text-[13.5px] font-semibold text-white">{title}</p>}
        <div className={cn('text-[13px] leading-relaxed text-zinc-400', title && 'mt-0.5')}>
          {children}
        </div>
        <div className="mt-2.5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => dismissHint(id)}
            className="rounded-full bg-white/10 px-3 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-white/15"
          >
            Got it
          </button>
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="rounded-full px-3 py-1 text-[12px] font-semibold text-zinc-300 transition-colors hover:bg-white/5 hover:text-white"
            >
              {action.label}
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => dismissHint(id)}
        className="absolute right-2.5 top-2.5 rounded-full p-1 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
      >
        <FiX size={14} />
      </button>
    </div>
  );
};
